import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';
import { GroupFeeCartLine } from '@pdqdev/pdq-shared/dist/utils/fee-per-item-group.types';
import { CheckoutApiService } from './api/checkout.api.service';

// A row points at a product in the cart rather than carrying it, so a product shown against two
// rules is still priced once.
export interface PreviewRow {
  ruleIndex: number | null;
  lineId: string | null;
  title: string | null;
  // Set when this row's product matches its rule but an earlier rule is the one charging it.
  chargedByRuleIndex?: number | null;
}

export interface ItemFeePreviewResult {
  cart: GroupFeeCartLine[];
  rows: PreviewRow[];
  available: boolean;
}

// The shape this endpoint returned before the cart and the rows were separated.
interface LegacyLine {
  ruleIndex: number | null;
  title: string | null;
  line: GroupFeeCartLine | null;
  chargedByRuleIndex?: number | null;
}

interface LegacyResponse {
  lines?: LegacyLine[];
  available?: boolean;
}

export interface ItemFeePreviewRequest {
  rules: Checkout.FeePerItemGroupRule[];
  overlapMode?: Checkout.RuleOverlapMode;
}

@Injectable({ providedIn: 'root' })
export class ItemFeePreviewService {
  constructor(private checkoutApi: CheckoutApiService) {}

  async preview(request: ItemFeePreviewRequest): Promise<ItemFeePreviewResult> {
    const response = await firstValueFrom(this.checkoutApi.post('admin/v1/item-fee-preview', request));
    return ItemFeePreviewService.read(response);
  }

  // Both response shapes are read for one release, so the two sides can deploy in either order.
  // An unreadable body is "unknown", never an empty cart, which would price the fee as nothing.
  private static read(response: LegacyResponse & Partial<ItemFeePreviewResult>): ItemFeePreviewResult {
    if (Array.isArray(response?.rows) && Array.isArray(response?.cart)) {
      return { cart: response.cart, rows: response.rows, available: response.available !== false };
    }
    if (Array.isArray(response?.lines)) {
      return { ...ItemFeePreviewService.fromLines(response.lines), available: response.available !== false };
    }
    return { cart: [], rows: [], available: false };
  }

  private static fromLines(lines: LegacyLine[]): Pick<ItemFeePreviewResult, 'cart' | 'rows'> {
    const cart: GroupFeeCartLine[] = [];
    const rows = lines.map((line) => {
      if (line?.line && !cart.some((inCart) => inCart.lineId === line.line.lineId)) {
        cart.push(line.line);
      }
      return {
        ruleIndex: line?.ruleIndex ?? null,
        lineId: line?.line?.lineId ?? null,
        title: line?.title ?? null,
        chargedByRuleIndex: line?.chargedByRuleIndex ?? null,
      };
    });
    return { cart, rows };
  }
}
